import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
/* --- import Components ----*/
import Layout from 'src/components/Layout';
import { Topbar } from 'src/components/Topbar';
import { Section } from 'src/components/Section';
import { HeroHomeWrapper } from 'src/components/Hero/elements';
import HeroContentLeft from 'src/components/LandingPage/redesign/HeroContentLeft';
import RedesignForm from 'src/components/LandingPage/redesign/RedesignForm';
import VideoSection from 'src/components/Section/VideoSection';
import ContentCardsSection from 'src/components/Section/ContentCardsSection';
import CtaBottomSection from 'src/components/LandingPage/redesign/CtaBottomSection';
/* --- import Icons ----*/
import { HTMLcolored, CSScolored, JScolored } from 'src/components/Icons/Techno';
/* --- import Styles ----*/
import colors from 'src/style/colors';
import { mediaQueries } from 'src/style/breakpoints';

const TechLogos = () => (
  <div css={{ display: 'flex', alignItems: 'flex-end', gap: 4, overflow: 'visible', padding: 6 }}>
    <div css={{ transform: 'rotate(-12deg)', overflow: 'visible' }}>
      <HTMLcolored css={{ display: 'block', width: 30, height: 34, overflow: 'visible' }} />
    </div>
    <div css={{ transform: 'rotate(6deg) translateY(-6px)', overflow: 'visible' }}>
      <CSScolored css={{ display: 'block', width: 29, height: 34, overflow: 'visible' }} />
    </div>
    <div css={{ transform: 'rotate(-4deg) translateY(-2px)', overflow: 'visible' }}>
      <JScolored css={{ display: 'block', width: 34, height: 34, overflow: 'visible' }} />
    </div>
  </div>
);

const LPApprendreACoderGratuitement = ({ location }) => {
  const data = useStaticQuery(graphql`
    query {
      xavierInterview: file(relativePath: { eq: "Xavier_interview.png" }) {
        childImageSharp {
          fluid(quality: 50, maxWidth: 900) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  return (
    <Layout
      pathname={location.pathname}
      businessType="B2C"
      pageName="LP Apprendre à coder gratuitement"
      pageType="landingPage"
      metaTitle="Apprendre à coder gratuitement - JavaScript, HTML et CSS | Le Reacteur"
      metaDescription="Découvrez les bases du code gratuitement avec Le Reacteur. 40h de formation en ligne : HTML, CSS et JavaScript. Accès immédiat, pour débutants."
      css={{ paddingTop: 0 }}
      noFooter
    >
      <Topbar minimal />

      {/* HERO SECTION */}
      <div
        css={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.purple,
          background: `radial-gradient(ellipse 80% 50% at 50% 100%, rgba(138, 125, 222, 0.6) 0%, ${colors.purple} 60%)`,
        }}
      >
        <Section theme="primary" hero landing css={{ backgroundColor: 'transparent' }}>
          <HeroHomeWrapper
            landing
            css={{
              '@media only screen and (max-width: 1199px)': {
                alignItems: 'center',
              },
            }}
          >
            {/* LEFT SIDE */}
            <HeroContentLeft
              title={
                <>
                  Maîtrisez les <span css={{ color: colors.yellow }}>bases du code</span>
                  <br />
                  gratuitement
                </>
              }
              subtitle={
                <>
                  <strong style={{ color: colors.yellow }}>40h de formation en ligne</strong> pour
                  apprendre HTML, CSS et JavaScript. Créée par les développeurs expérimentés du
                  Reacteur, l'une des meilleures écoles de code en France.
                </>
              }
              infoBox={
                <>
                  <strong>Débutant en code ? C'est fait pour vous.</strong>
                  <br />
                  40h pour voir si la programmation vous plait.
                </>
              }
              checklistItems={[
                { left: '100% gratuit', middle: 'En ligne', right: 'Accès immédiat' },
              ]}
              showLogos={false}
              statsLine1="🎓 +3000 élèves formés"
              statsRating="4,9/5"
              statsRatingLabel="avis vérifiés"
            />

            {/* RIGHT SIDE - Free Training Form */}
            <div
              css={{
                flex: '0 0 40%',
                maxWidth: '40%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignSelf: 'center',
                '@media only screen and (max-width: 1199px)': {
                  width: '80%',
                  maxWidth: '80%',
                  flex: '1',
                  marginTop: '3rem',
                },
                [mediaQueries.phoneOnly]: {
                  width: '100%',
                  maxWidth: '100%',
                  flex: '1',
                  alignSelf: 'stretch',
                  marginTop: '2rem',
                },
              }}
            >
              <RedesignForm
                formType="freeTraining"
                headerTag="🎓 FORMATION 100% GRATUITE"
                headerTitle="Découvrez le code en 40h"
                headerIcon={<TechLogos />}
                submitText="Recevoir l'accès immédiat"
                footerText="✅ Gratuit • Sans engagement"
                successComponent={(submittedValues) => (
                  <div css={{ textAlign: 'center', padding: '20px 0' }}>
                    <p
                      css={{
                        fontSize: 18,
                        color: colors.purpleDark,
                        fontWeight: 600,
                        marginBottom: 10,
                      }}
                    >
                      Merci {submittedValues?.firstName} !
                    </p>
                    <p css={{ fontSize: 16, color: colors.darkGrey }}>
                      Vérifiez votre boîte mail (et vos SPAMs) pour accéder à la formation gratuite.
                    </p>
                  </div>
                )}
              >
                <RedesignForm.Row>
                  <RedesignForm.FirstName />
                  <RedesignForm.LastName />
                </RedesignForm.Row>
                <RedesignForm.Email />
                <RedesignForm.Phone />
              </RedesignForm>
            </div>
          </HeroHomeWrapper>
        </Section>
      </div>

      {/* VIDEO SECTION */}
      <VideoSection
        title={
          <>
            Êtes-vous fait pour devenir <span css={{ color: colors.purple }}>Développeur</span> ?
          </>
        }
        subtitle="Intéressé par la tech ? Testez gratuitement si le développement vous plaît. À l'ère de l'IA, les développeurs les plus recherchés sont ceux qui maîtrisent les bases du code ET utilisent l'IA pour décupler leur efficacité."
        videoThumbnail={data.xavierInterview.childImageSharp.fluid}
        videoLink="https://www.youtube.com/embed/2KF0LMRZ7VE"
      />

      {/* CONTENT CARDS SECTION */}
      <ContentCardsSection
        title={
          <>
            Une formation gratuite pour{' '}
            <span css={{ color: colors.purple }}>découvrir le code</span>
          </>
        }
        cards={[
          {
            icon: '📚',
            title: 'Le contenu de la formation en e-learning',
            description:
              'Deux modules pour apprendre le HTML/CSS et le JavaScript. Des cours écrits et vidéos, des exercices à réaliser soi-même et des corrections. Tout est en français et accessible sur notre plateforme Apollo.',
          },
          {
            icon: '🎯',
            title: 'Se préparer au bootcamp',
            description:
              'Si vous décidez de rejoindre le bootcamp du Reacteur, cette formation en ligne vous permettra de préparer votre intégration, en commençant à mettre les mains dans le cambouis. Nous la recommandons à tous nos futurs élèves !',
          },
          {
            icon: '🚀',
            title: 'Comment accéder à la formation ?',
            description:
              'Remplissez le formulaire ci-dessus et vérifiez votre boîte mail pour la procédure de création de compte. Privilégiez une adresse Gmail et pensez à vérifier vos SPAMs.',
          },
        ]}
      />

      {/* CTA FINAL */}
      <CtaBottomSection
        onCtaClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        title="Prêt·e à découvrir le code ?"
        subtitle="Inscrivez-vous et accédez immédiatement à 40h de formation gratuite."
        buttonText="Je me lance !"
        footerText="✓ 100% gratuit · Sans engagement"
      />
    </Layout>
  );
};

export default LPApprendreACoderGratuitement;
